import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ensureArray, safeLength, safeFilter } from '../utils/arrayUtils';
import { 
  Bell, X, Check, CheckCheck, Filter, Search, Calendar,
  AlertCircle, CheckCircle, Info, AlertTriangle, XCircle,
  ShoppingCart, Wrench, DollarSign, Server, Shield, Database,
  User, Clock, ArrowRight, Settings, Trash2, Eye, EyeOff
} from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { NotificationCenter as NotificationCenterType } from '../types';
import { getNotificationDisplay, openNotificationTarget, NotificationChangeList } from '../utils/notificationDetails';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [notifications, setNotifications] = useState<NotificationCenterType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  // Load notifications when component opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiService.getNotifications();
      
      // Son 3 günün bildirimlerini filtrele
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      
      // 1 günden eski bildirimleri okundu işaretle
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      
      const filteredNotifications = response
        .filter(notification => new Date(notification.created_at) >= threeDaysAgo)
        .filter(notification => {
          // Teknisyen rolü admin/superadmin notification'larını görmesin
          if (user?.role === 'teknisyen' && notification.created_by_user) {
            return !['admin', 'superadmin'].includes(notification.created_by_user.role);
          }
          return true;
        })
        .map(notification => ({
          ...notification,
          is_read: notification.is_read || new Date(notification.created_at) < oneDayAgo
        }));
      
      setNotifications(filteredNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { value: 'all', label: t('notif.filter.all_types') },
    { value: 'info', label: t('notif.filter.info'), color: 'text-primary' },
    { value: 'success', label: t('notif.filter.success'), color: 'text-green-600' },
    { value: 'warning', label: t('notif.filter.warning'), color: 'text-yellow-600' },
    { value: 'error', label: t('notif.filter.error'), color: 'text-red-600' },
    { value: 'system', label: t('notif.filter.system'), color: 'text-purple-600' }
  ];

  const categoryOptions = [
    { value: 'all', label: t('notif.category.all') },
    { value: 'order', label: t('notif.category.order'), icon: ShoppingCart },
    { value: 'service', label: t('notif.category.service'), icon: Wrench },
    { value: 'capex', label: 'CAPEX', icon: DollarSign },
    { value: 'system', label: t('notif.category.system'), icon: Server },
    { value: 'backup', label: t('notif.category.backup'), icon: Database },
    { value: 'security', label: t('notif.category.security'), icon: Shield },
    { value: 'user', label: t('notif.category.user'), icon: User }
  ];

  const filteredNotifications = safeFilter(notifications, notification => {
    const display = getNotificationDisplay(notification, t, language);
    const searchable = [display.title, display.message, notification.message,
      ...display.changes.map(c => `${c.label} ${c.oldText ?? ''} ${c.newText ?? ''}`)].join(' ').toLowerCase();
    const matchesSearch = searchable.includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || notification.type === filterType;
    const matchesCategory = filterCategory === 'all' || notification.category === filterCategory;
    const matchesReadStatus = !showUnreadOnly || !notification.is_read;

    return matchesSearch && matchesType && matchesCategory && matchesReadStatus && !notification.is_dismissed;
  });

  const unreadCount = safeFilter(notifications, n => !n.is_read && !n.is_dismissed).length;

  const markAsRead = async (id: number) => {
    try {
      await apiService.markNotificationAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      window.dispatchEvent(new CustomEvent('notificationUpdate'));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/notifications/mark-all-read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      window.dispatchEvent(new CustomEvent('notificationUpdate'));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const dismissNotification = async (id: number) => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/notifications/${id}/dismiss`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_dismissed: true } : n)
      );
      window.dispatchEvent(new CustomEvent('notificationUpdate'));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const clearAllNotifications = async () => {
    if (confirm(t('notif.confirm_clear'))) {
      try {
        // Dismiss all notifications for current user
        const promises = notifications.map(n => 
          fetch(`${import.meta.env.VITE_API_URL}/notifications/${n.id}/dismiss`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          })
        );
        await Promise.all(promises);
        
        setNotifications(prev => 
          prev.map(n => ({ ...n, is_dismissed: true }))
        );
        window.dispatchEvent(new CustomEvent('notificationUpdate'));
      } catch (error) {
        console.error('Error clearing notifications:', error);
      }
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return CheckCircle;
      case 'warning':
        return AlertTriangle;
      case 'error':
        return XCircle;
      case 'system':
        return Server;
      default:
        return Info;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      case 'system':
        return 'text-purple-600';
      default:
        return 'text-primary';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500 bg-red-50';
      case 'high':
        return 'border-l-orange-500 bg-orange-50';
      case 'normal':
        return 'border-l-blue-500 bg-primary/10';
      case 'low':
        return 'border-l-gray-500 bg-gray-50 dark:bg-gray-700';
      default:
        return 'border-l-gray-500 bg-gray-50 dark:bg-gray-700';
    }
  };

  const getCategoryIcon = (category: string) => {
    return categoryOptions.find(c => c.value === category)?.icon || Bell;
  };

  const handleNotificationClick = (notification: NotificationCenterType) => {
    markAsRead(notification.id);

    // Mention bildirimlerini handle et
    if (notification.category === 'msg') {
      try {
        const metadata = JSON.parse(notification.metadata || '{}');
        if (metadata.messageId && metadata.groupChat && metadata.mention) {
          window.dispatchEvent(new CustomEvent('openGroupChat', {
            detail: { messageId: metadata.messageId }
          }));
          onClose();
          return;
        }
      } catch (error) {
        console.error('Error parsing notification metadata:', error);
      }
    }

    if (openNotificationTarget(notification)) {
      onClose();
    }
  };

  // Auto-refresh notifications every 30 seconds
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      loadNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Boş alana tıklama veya Esc ile kapat. Tıklamanın basıldığı ve bırakıldığı yer arka plan olmalı;
  // böylece panel içinde metin seçerken fare dışarıda bırakılınca yanlışlıkla kapanmaz.
  const backdropPressed = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Header'daki backdrop-blur, içindeki fixed öğeleri header kutusuna hapsediyordu (arka plan sadece
  // üst şeridi kaplıyor, boş alana tıklanamıyordu); bu yüzden body'ye portal ile render edilir.
  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[70] pt-16"
      onMouseDown={(e) => { backdropPressed.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (backdropPressed.current && e.target === e.currentTarget) onClose();
        backdropPressed.current = false;
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Bell className="w-6 h-6 text-primary mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('notif.title')}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('notif.unread', { count: unreadCount })}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={markAllAsRead}
                className="p-2 text-gray-400 hover:text-primary transition-colors"
                title={t('notif.mark_all_read')}
              >
                <CheckCheck className="w-5 h-5" />
              </button>
              <button
                onClick={clearAllNotifications}
                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                title={t('notif.clear_all')}
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('notif.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full text-sm"
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            >
              {typeOptions.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            >
              {categoryOptions.map(category => (
                <option key={category.value} value={category.value}>{category.label}</option>
              ))}
            </select>

            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={showUnreadOnly}
                onChange={(e) => setShowUnreadOnly(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-primary shadow-sm focus:border-primary/50 focus:ring focus:ring-primary/50 focus:ring-opacity-50"
              />
              <span className="text-gray-700 dark:text-gray-300">{t('notif.unread_only')}</span>
            </label>
          </div>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-96">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">{t('notif.loading')}</p>
            </div>
          ) : safeLength(filteredNotifications) === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('notif.not_found')}</h3>
              <p className="text-gray-600 dark:text-gray-400">{t('notif.no_match')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredNotifications.map((notification) => {
                const NotificationIcon = getNotificationIcon(notification.type);
                const CategoryIcon = getCategoryIcon(notification.category);
                const display = getNotificationDisplay(notification, t, language);
                
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors cursor-pointer border-l-4 ${
                      getPriorityColor(notification.priority)
                    } ${!notification.is_read ? 'bg-primary/10' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`flex-shrink-0 ${getNotificationColor(notification.type)}`}>
                        <NotificationIcon className="w-5 h-5" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <h4 className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                              {display.title}
                            </h4>
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-primary rounded-full"></div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <CategoryIcon className="w-4 h-4 text-gray-400" />
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              notification.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                              notification.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                              notification.priority === 'normal' ? 'bg-primary/20 text-primary-dark' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}>
                              {notification.priority === 'urgent' ? t('notif.priority.urgent') :
                               notification.priority === 'high' ? t('notif.priority.high') :
                               notification.priority === 'normal' ? t('notif.priority.normal') : t('notif.priority.low')}
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {display.message}
                        </p>
                        <NotificationChangeList changes={display.changes} t={t} className="text-xs mb-2" />
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              <span>{new Date(notification.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                            </div>
                            {notification.created_by_user && (
                              <div className="flex items-center gap-1">
                                {notification.created_by_user.avatar ? (
                                  <img
                                    src={notification.created_by_user.avatar}
                                    alt={notification.created_by_user.name}
                                    className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold" style={{ fontSize: '8px' }}>
                                      {notification.created_by_user.name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                <span>{notification.created_by_user.name}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            {notification.action_text && (
                              <div className="flex items-center text-xs text-primary">
                                <span>{notification.action_text}</span>
                                <ArrowRight className="w-3 h-3 ml-1" />
                              </div>
                            )}
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissNotification(notification.id);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title={t('notif.dismiss')}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {t('notif.showing', { count: safeLength(filteredNotifications) })}
            </div>
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('navigate', { detail: 'notification-settings' }));
                onClose();
              }}
              className="flex items-center text-sm text-primary hover:text-primary-dark transition-colors"
            >
              <Settings className="w-4 h-4 mr-1" />
              {t('notif.settings')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NotificationCenter;